import type { ArticlesData, Content, SiteConfig, VideosData } from "./types";

const markdownCache = new Map<string, string>();
const markdownPending = new Map<string, Promise<string>>();
let contentPending: Promise<Content> | null = null;

async function fetchJson<T>(pathname: string): Promise<T> {
  const response = await fetch(pathname);
  if (!response.ok) throw new Error(`加载失败：${pathname}（${response.status}）`);
  return response.json() as Promise<T>;
}

/** 按需加载单篇文章正文，并对并发请求去重。 */
export function loadArticleMarkdown(slug: string, file: string): Promise<string> {
  if (markdownCache.has(slug)) return Promise.resolve(markdownCache.get(slug) ?? "");
  const pending = markdownPending.get(slug);
  if (pending) return pending;

  const request = fetch(`/content/articles/${encodeURIComponent(file)}`)
    .then((response) => {
      if (!response.ok) throw new Error(`文章加载失败（${response.status}）`);
      return response.text();
    })
    .then((text) => {
      markdownCache.set(slug, text);
      markdownPending.delete(slug);
      return text;
    })
    .catch((error: unknown) => {
      markdownPending.delete(slug);
      throw error;
    });

  markdownPending.set(slug, request);
  return request;
}

/** 预加载全部文章正文，供全文搜索使用。 */
export async function loadAllMarkdown(data: ArticlesData): Promise<Map<string, string>> {
  await Promise.all(
    data.articles.map((article) => loadArticleMarkdown(article.slug, article.file).catch(() => ""))
  );
  return new Map(markdownCache);
}

/** 加载站点内容。模块级 Promise 可避免 StrictMode 下重复请求。 */
export function loadSiteContent(): Promise<Content> {
  if (contentPending) return contentPending;

  contentPending = Promise.all([
    fetchJson<VideosData>("/content/videos.json"),
    fetchJson<ArticlesData>("/content/articles/index.json"),
    fetchJson<SiteConfig>("/content/site.json").catch(
      (): SiteConfig => ({
        analytics: { provider: "none" },
        comments: { provider: "none" },
      })
    ),
  ])
    .then(([videosData, articlesData, site]) => {
      const categoryOrder = new Map(articlesData.categories.map((category, index) => [category, index]));
      const articles = [...articlesData.articles].sort(
        (left, right) =>
          (categoryOrder.get(left.category) ?? Number.MAX_SAFE_INTEGER) -
            (categoryOrder.get(right.category) ?? Number.MAX_SAFE_INTEGER) ||
          left.order - right.order
      );
      const videos = videosData.videos.filter((video) => !video.hidden);

      return {
        videosData: { ...videosData, videos },
        articlesData: { ...articlesData, articles },
        site,
      };
    })
    .catch((error: unknown) => {
      contentPending = null;
      throw error;
    });

  return contentPending;
}
